/**
 * 图片管理 — 系统管理框架内的视图模块
 * 分组卡片展示、批量操作、SSE 队列监控
 */

import { authFetch } from './api.js';
import { showToast, renderBlockError, formatNumber } from './utils.js';
import { registerViewCleanup, registerViewLoader } from './navigation.js';
import { setImageManagerListingId, getImageManagerListingId, getUserRole } from './state.js';
import { createImageManagerDetailController } from './image-manager-detail-controller.js';
import { createImageManagerFilterController } from './image-manager-filter-controller.js';
import { createImageManagerQueueController } from './image-manager-queue-controller.js';
import {
  formatImageManagerOwnerLabel as formatOwnerLabel,
  getImageManagerGroupKey as getGroupKey,
  parseImageManagerMetadata as parseMetadata,
  renderImageManagerGroupedListings,
} from './image-manager-listing-renderer.js';
import { formatBeijingDateKey } from '../time-beijing.js';

// ── Module State ────────────────────────────────────────────────
let groupedData = [];
let selectedIds = new Set();
let expandedListings = new Set();
let _imRefreshDebounce = null;
let _imRefreshInFlight = false;
let _returnTo = 'listings';
let initialized = false;
let controlsBound = false;
let globalEventsBound = false;

const imageManagerFilterController = createImageManagerFilterController({
  getListingId: getImageManagerListingId,
  setListingId: setImageManagerListingId,
  getUserRole,
  refreshGroups: () => refreshImageGroups(),
  resetGroupUi: () => {
    expandedListings.clear();
    selectedIds.clear();
    updateBulkBar();
  },
});

const imageManagerQueueController = createImageManagerQueueController({
  isViewActive: () => {
    const view = document.getElementById('view-image-manager');
    return Boolean(view?.classList.contains('active') && !document.hidden);
  },
  scheduleRefresh: () => scheduleRefreshImageGroups(),
});

const imageManagerDetailController = createImageManagerDetailController({
  getImage: (listingIndex, imageIndex) => groupedData[listingIndex]?.images?.[imageIndex] || null,
  parseMetadata,
  formatOwnerLabel,
  scheduleRefresh: () => scheduleRefreshImageGroups(),
});

/** 按状态统计：已完成 vs 生成中（以列表数据为准，避免角标把两者相加） */
function resolveGroupCounts(group) {
  const images = group?.images || [];
  const completed = images.filter((i) => i.status === 'completed' && i.file_url);
  const inflight = images.filter(
    (i) => i.status === 'pending' || i.status === 'processing'
  );
  return {
    completed: completed.length,
    inflight: inflight.length,
  };
}

// ── Init ────────────────────────────────────────────────────────
export function initImageManager() {
  if (!initialized) {
    initialized = true;
    registerViewLoader('image-manager', () => {
      imageManagerFilterController.hydrateSegment();
      const urlListingId = new URLSearchParams(window.location.search).get('listingId');
      if (!urlListingId) {
        setImageManagerListingId(null);
        imageManagerFilterController.clearListingIdFromUrl();
      }
      try {
        imageManagerQueueController.ensureConnection();
      } catch (err) {
        console.warn('[ImageManager] SSE unavailable, using poll only:', err);
      }
      imageManagerQueueController.startPolling();
      return refreshImageGroups();
    });
    registerViewCleanup('image-manager', () => {
      imageManagerQueueController.cleanup();
      imageManagerQueueController.stopPolling();
      clearTimeout(_imRefreshDebounce);
      _imRefreshDebounce = null;
    });

    const urlParams = new URLSearchParams(window.location.search);
    const urlListingId = urlParams.get('listingId');
    if (urlListingId) {
      setImageManagerListingId(parseInt(urlListingId, 10));
    }

  }

  bindImageManagerControls();
  bindImageManagerGlobalEvents();
}

function bindImageManagerControls() {
  if (controlsBound || !document.getElementById('view-image-manager')) return;
  controlsBound = true;
  imageManagerFilterController.bindSegmentTabs();
  bindImageManagerListingEvents();

  const backBtn = document.getElementById('image-manager-back-btn');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      imageManagerQueueController.cleanup();
      imageManagerQueueController.stopPolling();
      setImageManagerListingId(null);
      import('./navigation.js').then(({ switchView }) => {
        switchView(_returnTo);
      });
    });
  }

  // Bulk bar buttons
  const bulkSelectAll = document.getElementById('im-bulk-select-all');
  if (bulkSelectAll) {
    bulkSelectAll.addEventListener('click', () => {
      const allIds = getAllVisibleImageIds();
      if (selectedIds.size === allIds.length) {
        selectedIds.clear();
      } else {
        allIds.forEach((id) => selectedIds.add(id));
      }
      renderGroupedListings();
      updateBulkBar();
    });
  }

  const bulkDownload = document.getElementById('im-bulk-download');
  if (bulkDownload) bulkDownload.addEventListener('click', handleBulkDownload);

  const bulkDelete = document.getElementById('im-bulk-delete');
  if (bulkDelete) bulkDelete.addEventListener('click', handleBulkDelete);

  const bulkClear = document.getElementById('im-bulk-clear');
  if (bulkClear) {
    bulkClear.addEventListener('click', () => {
      selectedIds.clear();
      renderGroupedListings();
      updateBulkBar();
    });
  }

  const refreshBtn = document.getElementById('im-refresh-btn');
  if (refreshBtn) refreshBtn.addEventListener('click', () => refreshImageGroups());
}

function bindImageManagerGlobalEvents() {
  const closeBtn = document.getElementById('im-detail-close');
  if (closeBtn) closeBtn.addEventListener('click', () => imageManagerDetailController.close());

  const overlay = document.getElementById('im-detail-overlay');
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) imageManagerDetailController.close();
    });
  }

  const regenBtn = document.getElementById('im-btn-regenerate');
  if (regenBtn) regenBtn.addEventListener('click', () => imageManagerDetailController.regenerate());

  const downloadBtn = document.getElementById('im-btn-download');
  if (downloadBtn) downloadBtn.addEventListener('click', () => imageManagerDetailController.download());

  const previewBtn = document.getElementById('im-btn-preview');
  if (previewBtn) previewBtn.addEventListener('click', () => imageManagerDetailController.openPreview());

  const previewTrigger = document.getElementById('im-detail-preview-trigger');
  if (previewTrigger) {
    previewTrigger.addEventListener('click', () => imageManagerDetailController.openPreview());
    previewTrigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        imageManagerDetailController.openPreview();
      }
    });
  }

  if (globalEventsBound) return;
  globalEventsBound = true;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(_imRefreshDebounce);
    } else {
      const view = document.getElementById('view-image-manager');
      if (view?.classList.contains('active')) imageManagerQueueController.ensureConnection();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (document.querySelector('.im-image-lightbox')) {
      imageManagerDetailController.closePreview();
      return;
    }
    imageManagerDetailController.close();
  });
}

// ── Public: open from other views ───────────────────────────────
export function openImageManager(listingId, returnTo = 'listings') {
  _returnTo = returnTo;
  setImageManagerListingId(listingId);
  import('./navigation.js').then(({ switchView }) => {
    switchView('image-manager');
  });
}

// ── Load & Render ───────────────────────────────────────────────
async function refreshImageGroups() {
  if (_imRefreshInFlight) return;
  _imRefreshInFlight = true;
  const subtitleEl = document.getElementById('image-manager-subtitle');
  const loading = document.getElementById('im-images-loading');
  const empty = document.getElementById('im-images-empty');
  const container = document.getElementById('im-listings-container');

  if (loading) loading.hidden = false;
  if (empty) empty.hidden = true;
  if (container) {
    container.querySelectorAll('.im-listing-card').forEach((el) => el.remove());
  }
  if (subtitleEl) subtitleEl.textContent = '加载中...';

  try {
    const resp = await authFetch(imageManagerFilterController.buildGroupedApiUrl());
    if (!resp.ok) throw new Error(`Failed to load images (HTTP ${resp.status})`);
    const data = await resp.json();

    if (loading) loading.hidden = true;

    if (!data.listings || data.listings.length === 0) {
      groupedData = [];
      imageManagerFilterController.updateEmptyStateCopy();
      if (empty) empty.hidden = false;
      if (subtitleEl) {
        imageManagerFilterController.renderEmptySubtitle(subtitleEl);
      }
      return;
    }

    groupedData = data.listings;

    const highlightId = imageManagerFilterController.resolveListingFilter();
    if (highlightId) {
      groupedData
        .filter((g) => String(g.listing_id) === String(highlightId))
        .forEach((g) => expandedListings.add(getGroupKey(g)));
    } else if (groupedData.length === 1) {
      expandedListings.add(getGroupKey(groupedData[0]));
    }

    renderGroupedListings();

    const totals = groupedData.reduce(
      (acc, g) => {
        const c = resolveGroupCounts(g);
        acc.completed += c.completed;
        acc.inflight += c.inflight;
        return acc;
      },
      { completed: 0, inflight: 0 }
    );
    if (subtitleEl) {
      const inflightHint = totals.inflight > 0 ? ` (+${totals.inflight} 生成中)` : '';
      const segLabel = imageManagerFilterController.getSegment() === 'standalone' ? '个仅生图项目' : '个 Listing';
      subtitleEl.textContent = `${groupedData.length} ${segLabel} · 共 ${totals.completed} 张${inflightHint}`;
    }
  } catch (err) {
    console.error('[ImageManager] Refresh failed:', err);
    if (loading) loading.hidden = true;
    if (empty) {
      empty.hidden = false;
      renderBlockError(empty, `加载失败: ${err.message}`);
    }
    if (subtitleEl) subtitleEl.textContent = '加载失败';
  } finally {
    _imRefreshInFlight = false;
  }
}

function renderGroupedListings() {
  renderImageManagerGroupedListings({
    groupedData,
    selectedIds,
    expandedListings,
    formatNumber,
    onRenameProject: handleRenameProject,
    resolveGroupCounts,
  });
}

async function handleRenameProject(btn) {
  const listingId = btn.dataset.listingId;
  const current = btn.dataset.listingName || '';
  const next = window.prompt('项目名称', current);
  if (next == null) return;
  const trimmed = next.trim();
  if (!trimmed) {
    showToast('名称不能为空', true);
    return;
  }
  try {
    const resp = await authFetch(`/api/saved-listings/${listingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }
    showToast('名称已更新');
    refreshImageGroups();
  } catch (err) {
    showToast(err.message || '重命名失败', true);
  }
}

// ── Event Delegation on #im-listings-container ──────────────────
function bindImageManagerListingEvents() {
  const container = document.getElementById('im-listings-container');
  if (!container || container._imBound) return;
  container._imBound = true;

  container.addEventListener('click', (e) => {
      // Toggle expand/collapse
      const header = e.target.closest('.im-listing-header');
      if (header && container.contains(header)) {
        const idx = parseInt(header.dataset.listingIdx, 10);
        const group = groupedData[idx];
        if (!group) return;
        const key = getGroupKey(group);
        if (expandedListings.has(key)) {
          expandedListings.delete(key);
        } else {
          expandedListings.add(key);
        }
        renderGroupedListings();
        return;
      }

      // Open detail overlay (click on image area, not checkbox)
      const card = e.target.closest('.image-card');
      if (card && container.contains(card)) {
        if (e.target.closest('.im-image-checkbox')) return;
        const li = parseInt(card.dataset.listingIdx, 10);
        const ii = parseInt(card.dataset.imageIdx, 10);
        const img = groupedData[li]?.images?.[ii];
        if (img && (img.status || 'completed') === 'completed') {
          imageManagerDetailController.show(li, ii);
        }
      }
  });

  container.addEventListener('change', (e) => {
      // Individual image checkbox
      const cb = e.target.closest('.im-image-checkbox');
      if (cb && container.contains(cb)) {
        const id = parseInt(cb.dataset.imageId, 10);
        if (cb.checked) {
          selectedIds.add(id);
        } else {
          selectedIds.delete(id);
        }
        updateBulkBar();
        // Selection is a presentational class; do not write an unscoped inline outline.
        const card = cb.closest('.image-card');
        card?.classList.toggle('is-selected', cb.checked);
        // Update group select-all state
        const listingIdx = parseInt(cb.closest('.image-card')?.dataset.listingIdx, 10);
        if (!isNaN(listingIdx)) updateSelectAllState(listingIdx);
        return;
      }

      // Select-all checkbox for a listing group
      const selectAll = e.target.closest('.im-select-all');
      if (selectAll && container.contains(selectAll)) {
        const listingIdx = parseInt(selectAll.dataset.listingIdx, 10);
        const group = groupedData[listingIdx];
        if (!group) return;
        const ids = group.images.map((img) => img.id);
        if (selectAll.checked) {
          ids.forEach((id) => selectedIds.add(id));
        } else {
          ids.forEach((id) => selectedIds.delete(id));
        }
        renderGroupedListings();
        updateBulkBar();
      }
  });
}

function updateSelectAllState(listingIdx) {
  const group = groupedData[listingIdx];
  if (!group) return;
  const selectAllCb = document.querySelector(`.im-select-all[data-listing-idx="${listingIdx}"]`);
  if (!selectAllCb) return;
  const ids = group.images.map((img) => img.id);
  const allChecked = ids.length > 0 && ids.every((id) => selectedIds.has(id));
  selectAllCb.checked = allChecked;
}

function getAllVisibleImageIds() {
  const ids = [];
  groupedData.forEach((group) => {
    group.images.forEach((img) => ids.push(img.id));
  });
  return ids;
}

// ── Bulk Bar ────────────────────────────────────────────────────
function updateBulkBar() {
  const bar = document.getElementById('im-bulk-bar');
  const countEl = document.getElementById('im-bulk-count');
  if (!bar) return;

  if (selectedIds.size > 0) {
    bar.hidden = false;
    if (countEl) countEl.textContent = `已选 ${selectedIds.size} 张`;
  } else {
    bar.hidden = true;
  }
}

async function handleBulkDelete() {
  if (selectedIds.size === 0) return;
  const count = selectedIds.size;
  if (!confirm(`确定删除选中的 ${count} 张图片？此操作不可撤销。`)) return;

  try {
    const resp = await authFetch('/api/image/assets/bulk-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(selectedIds) }),
    });
    const data = await resp.json();
    if (!resp.ok || data.error) throw new Error(data.error || `HTTP ${resp.status}`);
    showToast(`已删除 ${data.deleted || count} 张图片`, 'success');
    selectedIds.clear();
    updateBulkBar();
    refreshImageGroups().catch(() => {});
  } catch (err) {
    showToast('删除失败: ' + err.message, 'error');
  }
}

async function handleBulkDownload() {
  if (selectedIds.size === 0) return;
  const count = selectedIds.size;

  try {
    showToast(`正在打包 ${count} 张图片...`, 'info');
    const resp = await authFetch('/api/image/assets/download-zip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(selectedIds) }),
    });

    if (!resp.ok) {
      const errData = await resp.json().catch(() => null);
      throw new Error(errData?.error || `HTTP ${resp.status}`);
    }

    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `images-${formatBeijingDateKey()}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast(`已下载 ${count} 张图片`, 'success');
  } catch (err) {
    showToast('下载失败: ' + err.message, 'error');
  }
}

function scheduleRefreshImageGroups() {
  const view = document.getElementById('view-image-manager');
  if (!view?.classList.contains('active') || document.hidden) return;

  clearTimeout(_imRefreshDebounce);
  _imRefreshDebounce = setTimeout(() => {
    const prevExpanded = new Set(expandedListings);
    const prevSelected = new Set(selectedIds);
    refreshImageGroups().then(() => {
      prevExpanded.forEach((k) => expandedListings.add(k));
      prevSelected.forEach((id) => selectedIds.add(id));
      renderGroupedListings();
      updateBulkBar();
    });
  }, 1200);
}
